import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Plus, Trash2, ImageIcon, X, Loader2, MoreVertical, Pencil } from 'lucide-react';
import { useCharacterNotes } from '../hooks/useCharacterNotes';
import { useCharacters } from '../hooks';
import TierBadge from '../components/common/TierBadge';

import { EditCharacterModal } from '../components';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import Skeleton from '../components/common/Skeleton';

const API_BASE = 'https://api.cloudnine.biz.id';
const DEFAULT_IMAGE = 'https://api.cloudnine.biz.id/assets/img/defaultchar.png';
const MAX_IMAGES = 4;

/**
 * Parse image field — backward-compatible: string → [string], JSON array → array
 */
const parseImages = (img) => {
    if (!img) return [];
    if (Array.isArray(img)) return img;
    try {
        const parsed = JSON.parse(img);
        return Array.isArray(parsed) ? parsed : [img];
    } catch {
        return img ? [img] : [];
    }
};

/**
 * Serialize images array → JSON string for API
 */
const serializeImages = (arr) => {
    if (!arr || arr.length === 0) return null;
    return JSON.stringify(arr);
};

const detailStyles = `
    .masonry-grid {
        columns: 1;
        column-gap: 1rem;
    }
    @media (min-width: 640px) {
        .masonry-grid { columns: 2; }
    }
    @media (min-width: 1024px) {
        .masonry-grid { columns: 3; }
    }
    @media (min-width: 1280px) {
        .masonry-grid { columns: 4; }
    }
    .masonry-item {
        break-inside: avoid;
        margin-bottom: 1rem;
    }
    .note-card {
        transition: box-shadow 0.2s, transform 0.15s;
    }
    .note-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0,0,0,0.12);
    }
    .note-card.drag-over {
        outline: 2px dashed #6366f1;
        outline-offset: -2px;
        background: rgba(99, 102, 241, 0.05) !important;
    }
    @keyframes card-enter {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    .card-enter { animation: card-enter 0.3s ease-out; }
    .dragging-item { opacity: 0.4; }
    .img-thumb {
        width: 36px;
        height: 36px;
        border-radius: 6px;
        object-fit: cover;
        cursor: pointer;
        transition: transform 0.15s, box-shadow 0.15s;
    }
    .img-thumb:hover {
        transform: scale(1.08);
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }
    @keyframes divine-name-shift {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
    }
    .divine-name {
        background: linear-gradient(90deg, #67e8e9, #c4b5fd, #fca5f1, #c4b5fd, #67e8e9);
        background-size: 300% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        animation: divine-name-shift 4s ease-in-out infinite;
    }
    @keyframes lightbox-in {
        from { opacity: 0; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1); }
    }
    .lightbox-enter { animation: lightbox-in 0.2s ease-out; }
    @keyframes ctx-in {
        from { opacity: 0; transform: scale(0.95) translateY(-4px); }
        to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    .ctx-menu { animation: ctx-in 0.12s ease-out; }
`;

/**
 * Upload file ke server
 */
const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/upload.php`, { method: 'POST', body: formData });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Upload failed');
    return data.url;
};

/**
 * Lightbox image preview
 */
const ImagePreviewModal = ({ src, onClose }) => {
    if (!src) return null;
    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="lightbox-enter max-w-[90vw] max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
                <img src={src} alt="" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain" />
                <button onClick={onClose} className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-black/80 hover:bg-black text-white flex items-center justify-center transition-colors shadow-lg">
                    <X size={14} />
                </button>
            </div>
        </div>,
        document.body
    );
};

/**
 * Context menu (3-dot)
 */
const ContextMenu = ({ isDarkMode, onAddImage, onDelete, imageCount, uploading, children }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            onAddImage(files);
            setOpen(false);
        }
        e.target.value = '';
    };

    return (
        <div ref={menuRef} className="relative shrink-0">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                className={`
                    p-1 rounded transition-colors opacity-0 group-hover:opacity-100
                    ${isDarkMode ? 'hover:bg-slate-700 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}
                `}
            >
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <MoreVertical size={12} />}
            </button>
            {open && (
                <div className={`
                    ctx-menu absolute right-0 top-full mt-1 z-50 min-w-[140px] rounded-lg shadow-xl border py-1
                    ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                `}>
                    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={imageCount >= MAX_IMAGES || uploading}
                        className={`
                            w-full flex items-center gap-2 px-3 py-1.5 text-[10px] transition-colors
                            ${imageCount >= MAX_IMAGES ? 'opacity-30 cursor-not-allowed' : ''}
                            ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-50 text-slate-600'}
                        `}
                    >
                        <ImageIcon size={11} />
                        {imageCount >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : 'Add image'}
                    </button>
                    {children}
                    <button
                        onClick={() => { onDelete(); setOpen(false); }}
                        className={`
                            w-full flex items-center gap-2 px-3 py-1.5 text-[10px] transition-colors
                            ${isDarkMode ? 'hover:bg-rose-500/20 text-rose-400' : 'hover:bg-rose-50 text-rose-500'}
                        `}
                    >
                        <Trash2 size={11} />
                        Delete item
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * Inline editable text
 */
const InlineEdit = ({ value, onSave, placeholder, className, multiline = false, isDarkMode, onPaste }) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value || '');
    const inputRef = useRef(null);

    const handleStart = () => {
        setText(value || '');
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSave = () => {
        setEditing(false);
        if (text.trim() !== (value || '').trim()) {
            onSave(text.trim());
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !multiline) { e.preventDefault(); handleSave(); }
        if (e.key === 'Enter' && multiline && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSave(); }
        if (e.key === 'Escape') { setText(value || ''); setEditing(false); }
    };

    const handlePaste = (e) => {
        if (onPaste) {
            const items = Array.from(e.clipboardData?.items || []);
            const imageItems = items.filter(i => i.type.startsWith('image/'));
            if (imageItems.length > 0) {
                e.preventDefault();
                const files = imageItems.map(i => i.getAsFile()).filter(Boolean);
                onPaste(files);
            }
        }
    };

    if (editing) {
        const Tag = multiline ? 'textarea' : 'input';
        return (
            <Tag
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={placeholder}
                rows={multiline ? 3 : undefined}
                className={`
                    w-full bg-transparent outline-none resize-none
                    ${isDarkMode ? 'text-slate-200 placeholder-slate-600' : 'text-slate-700 placeholder-slate-400'}
                    ${className || ''}
                `}
            />
        );
    }

    return (
        <div
            onClick={handleStart}
            className={`
                cursor-text min-h-[1.5em] rounded px-1 -mx-1 transition-colors whitespace-pre-wrap break-words
                ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'}
                ${!value ? (isDarkMode ? 'text-slate-600' : 'text-slate-400') : ''}
                ${className || ''}
            `}
        >
            {value || placeholder}
        </div>
    );
};

/**
 * Row of image thumbnails (max 4)
 */
const ImageThumbs = ({ images, onRemove, onPreview, isDarkMode }) => {
    if (!images || images.length === 0) return null;
    return (
        <div className="flex gap-1.5 mt-1 flex-wrap">
            {images.map((url, i) => (
                <div key={i} className="relative group/img shrink-0">
                    <img src={url} alt="" className="img-thumb" loading="lazy" onClick={() => onPreview(url)} />
                    <button
                        onClick={() => onRemove(i)}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                        title="Remove"
                    >
                        <X size={8} />
                    </button>
                </div>
            ))}
        </div>
    );
};

/**
 * Single note item (child) — draggable, multi-image, 3-dot menu, Ctrl+V paste
 */
const NoteItem = ({ item, cardId, onUpdate, onDelete, isDarkMode, draggingId, onDragStart, onDragEnd, onPreviewImage }) => {
    const isDragging = draggingId === item.id;
    const [uploading, setUploading] = useState(false);
    const images = parseImages(item.image);

    const handleAddImages = async (files) => {
        const remaining = MAX_IMAGES - images.length;
        if (remaining <= 0) return;
        const toUpload = files.slice(0, remaining);
        setUploading(true);
        try {
            const urls = await Promise.all(toUpload.map(f => uploadFile(f)));
            const updated = [...images, ...urls].slice(0, MAX_IMAGES);
            onUpdate(item.id, { image: serializeImages(updated) });
        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = (index) => {
        const updated = images.filter((_, i) => i !== index);
        onUpdate(item.id, { image: serializeImages(updated) });
    };

    const handlePasteImages = (files) => {
        handleAddImages(files);
    };

    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData('application/json', JSON.stringify({ itemId: item.id, fromCardId: cardId }));
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(item.id);
            }}
            onDragEnd={() => onDragEnd()}
            className={`
                group flex items-start gap-2 py-1.5 px-2 rounded-lg transition-colors cursor-grab active:cursor-grabbing
                ${isDragging ? 'dragging-item' : ''}
                ${isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}
            `}
        >
            {/* Drag handle */}
            <div className={`mt-1.5 shrink-0 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`}>
                <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor">
                    <circle cx="2" cy="2" r="1.2" /><circle cx="6" cy="2" r="1.2" />
                    <circle cx="2" cy="6" r="1.2" /><circle cx="6" cy="6" r="1.2" />
                    <circle cx="2" cy="10" r="1.2" /><circle cx="6" cy="10" r="1.2" />
                </svg>
            </div>

            <div className="flex-1 min-w-0">
                <InlineEdit
                    value={item.content}
                    onSave={(content) => onUpdate(item.id, { content })}
                    placeholder="Write something..."
                    className="text-xs leading-relaxed"
                    multiline
                    isDarkMode={isDarkMode}
                    onPaste={handlePasteImages}
                />
                <ImageThumbs
                    images={images}
                    onRemove={handleRemoveImage}
                    onPreview={onPreviewImage}
                    isDarkMode={isDarkMode}
                />
            </div>

            {/* 3-dot context menu */}
            <ContextMenu
                isDarkMode={isDarkMode}
                onAddImage={handleAddImages}
                onDelete={() => onDelete(item.id, cardId)}
                imageCount={images.length}
                uploading={uploading}
            />
        </div>
    );
};

/**
 * Form untuk menambah item baru — multi-image, Ctrl+V paste
 */
const AddItemForm = ({ onSubmit, onCancel, isDarkMode }) => {
    const [content, setContent] = useState('');
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const textRef = useRef(null);

    const handleUpload = async (files) => {
        const remaining = MAX_IMAGES - images.length;
        if (remaining <= 0) return;
        const toUpload = Array.from(files).slice(0, remaining);
        setUploading(true);
        try {
            const urls = await Promise.all(toUpload.map(f => uploadFile(f)));
            setImages(prev => [...prev, ...urls].slice(0, MAX_IMAGES));
        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const handlePaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItems = items.filter(i => i.type.startsWith('image/'));
        if (imageItems.length > 0) {
            e.preventDefault();
            const files = imageItems.map(i => i.getAsFile()).filter(Boolean);
            handleUpload(files);
        }
    };

    const handleSubmit = () => {
        if (!content.trim() && images.length === 0) return;
        onSubmit({ content: content.trim(), image: serializeImages(images) });
        setContent('');
        setImages([]);
    };

    return (
        <div className={`px-3 pb-3 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
            <textarea
                ref={textRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handlePaste}
                placeholder="Write something... (Ctrl+V to paste image)"
                rows={2}
                className={`
                    w-full mt-2 px-2.5 py-1.5 rounded-lg text-xs outline-none resize-none
                    ${isDarkMode
                        ? 'bg-slate-700/50 text-slate-200 placeholder-slate-500 border border-slate-600 focus:border-indigo-500'
                        : 'bg-slate-50 text-slate-700 placeholder-slate-400 border border-slate-200 focus:border-indigo-500'
                    }
                `}
                autoFocus
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmit(); }
                    if (e.key === 'Escape') onCancel();
                }}
            />

            {/* Image thumbnails */}
            {images.length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                    {images.map((url, i) => (
                        <div key={i} className="relative group/img shrink-0">
                            <img src={url} alt="" className="img-thumb" />
                            <button
                                onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                            >
                                <X size={8} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 mt-2">
                <button
                    onClick={handleSubmit}
                    disabled={(!content.trim() && images.length === 0) || uploading}
                    className="text-[10px] px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-40"
                >
                    Add
                </button>
                <button
                    onClick={onCancel}
                    className={`text-[10px] px-2.5 py-1 rounded-md transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                    Cancel
                </button>

                <div className="flex-1" />

                {/* File pick button */}
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
                />
                <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || images.length >= MAX_IMAGES}
                    className={`
                        p-1.5 rounded-md transition-colors
                        ${uploading ? 'animate-pulse' : ''}
                        ${images.length >= MAX_IMAGES ? 'opacity-30' : ''}
                        ${isDarkMode ? 'hover:bg-slate-700 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}
                    `}
                    title={images.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : 'Add image'}
                >
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                </button>
            </div>
        </div>
    );
};

/**
 * Kanban-style note card (parent) — drop target
 */
const NoteCard = ({ card, onUpdate, onDelete, onAddItem, onMoveItem, isDarkMode, draggingId, onDragStart, onDragEnd, onPreviewImage }) => {
    const [adding, setAdding] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            if (data.fromCardId !== card.id) {
                onMoveItem(data.itemId, data.fromCardId, card.id);
            }
        } catch { }
    };

    const handleAddSubmit = async ({ content, image }) => {
        await onAddItem(card.id, { content, image });
        setAdding(false);
    };

    return (
        <div className="masonry-item card-enter">
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                    note-card rounded-xl border
                    ${dragOver ? 'drag-over' : ''}
                    ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-200'}
                `}
            >
                {/* Card Header */}
                <div className={`
                    flex items-center justify-between px-3.5 py-2.5 border-b
                    ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}
                `}>
                    <InlineEdit
                        value={card.title}
                        onSave={(title) => onUpdate(card.id, { title })}
                        placeholder="Card title..."
                        className="text-xs font-semibold flex-1"
                        isDarkMode={isDarkMode}
                    />
                    <div className="flex items-center gap-0.5 shrink-0 ml-2">
                        <span className={`text-[9px] mr-1 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                            {(card.children || []).length}
                        </span>
                        <button
                            onClick={() => setAdding(true)}
                            className={`
                                p-1 rounded transition-colors
                                ${isDarkMode ? 'hover:bg-slate-700 text-slate-500 hover:text-slate-300' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}
                            `}
                            title="Add item"
                        >
                            <Plus size={12} />
                        </button>
                        <button
                            onClick={() => onDelete(card.id)}
                            className={`
                                p-1 rounded transition-colors
                                ${isDarkMode ? 'hover:bg-rose-500/20 text-slate-500 hover:text-rose-400' : 'hover:bg-rose-50 text-slate-400 hover:text-rose-500'}
                            `}
                            title="Delete card"
                        >
                            <Trash2 size={12} />
                        </button>
                    </div>
                </div>

                {/* Items */}
                <div className="px-2 py-1.5 min-h-[2rem]">
                    {(card.children || []).length === 0 && !adding && (
                        <p className={`text-[10px] px-2 py-3 text-center ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                            {draggingId ? 'Drop here' : 'No items yet'}
                        </p>
                    )}
                    {(card.children || []).map((item) => (
                        <NoteItem
                            key={item.id}
                            item={item}
                            cardId={card.id}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            isDarkMode={isDarkMode}
                            draggingId={draggingId}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onPreviewImage={onPreviewImage}
                        />
                    ))}
                </div>

                {/* Add Item Form */}
                {adding && (
                    <AddItemForm
                        onSubmit={handleAddSubmit}
                        onCancel={() => setAdding(false)}
                        isDarkMode={isDarkMode}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * Halaman detail karakter dengan kanban masonry
 */
const CharacterDetailContent = ({ character, isDarkMode, onBack, onCharacterUpdated }) => {
    const { notes, loading, error, addCard, addItem, updateNote, deleteNote, moveItem } = useCharacterNotes(character?.id);
    const { updateCharacter, uploadImage } = useCharacters();
    const [addingCard, setAddingCard] = useState(false);
    const [newCardTitle, setNewCardTitle] = useState('');
    const [draggingId, setDraggingId] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [editingCharacter, setEditingCharacter] = useState(null);
    const [currentCharacter, setCurrentCharacter] = useState(character);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    // Sync if parent passes new character
    useEffect(() => { setCurrentCharacter(character); }, [character]);

    if (!currentCharacter) return null;

    const displayImage = currentCharacter.image || DEFAULT_IMAGE;
    const isDivine = (currentCharacter.tier || '').toLowerCase() === 'divine';

    const handleEditSubmit = async (data) => {
        const result = await updateCharacter(data);
        if (result) {
            setCurrentCharacter(prev => ({ ...prev, ...result }));
            if (onCharacterUpdated) onCharacterUpdated(result);
        }
        return !!result;
    };

    const handleDeleteRequest = (id, parentId) => {
        if (parentId) {
            // Delete Item
            setConfirmModal({
                isOpen: true,
                title: 'Delete Item',
                message: 'Are you sure you want to delete this note item?',
                onConfirm: () => deleteNote(id, parentId),
                confirmText: 'Delete',
                confirmColor: 'rose'
            });
        } else {
            // Delete Card
            setConfirmModal({
                isOpen: true,
                title: 'Delete Card',
                message: 'This will delete the card and all items within it. Continue?',
                onConfirm: () => deleteNote(id),
                confirmText: 'Delete All',
                confirmColor: 'rose'
            });
        }
    };

    const handleAddCard = async () => {
        if (!newCardTitle.trim()) return;
        await addCard(newCardTitle.trim());
        setNewCardTitle('');
        setAddingCard(false);
    };

    return (
        <div className="space-y-5 pb-20 md:pb-0 animate-fade-in">
            <style>{detailStyles}</style>

            {/* Back button */}
            <button
                onClick={onBack}
                className={`
                    flex items-center gap-2 text-xs transition-colors rounded-lg px-3 py-1.5
                    ${isDarkMode
                        ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }
                `}
            >
                <ArrowLeft size={14} />
                Back to Collection
            </button>

            {/* Character Header */}
            <div className={`
                relative overflow-hidden rounded-2xl border
                ${isDarkMode ? 'bg-slate-800/60 border-slate-700/50' : 'bg-white border-slate-200'}
            `}>
                <div className="flex items-center gap-5 p-5">
                    <div className="relative shrink-0 cursor-pointer" onClick={() => setPreviewImage(displayImage)}>
                        <img src={displayImage} alt={currentCharacter.name} className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover shadow-md hover:shadow-lg transition-shadow" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 mb-1">
                            <h1 className={`text-lg md:text-xl font-bold truncate ${isDivine ? 'divine-name' : ''}`}>
                                {currentCharacter.name}
                            </h1>
                            <TierBadge tier={currentCharacter.tier} isDarkMode={isDarkMode} size="sm" />
                        </div>
                        <p className={`text-xs md:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {currentCharacter.title}
                        </p>
                        {currentCharacter.race && (
                            <p className={`text-[10px] md:text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {currentCharacter.race}
                            </p>
                        )}
                    </div>
                    {/* Edit button */}
                    <button
                        onClick={() => setEditingCharacter(currentCharacter)}
                        className={`
                            shrink-0 p-2 rounded-lg transition-colors
                            ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}
                        `}
                        title="Edit character"
                    >
                        <Pencil size={16} />
                    </button>
                </div>
            </div>

            {/* Notes Section Header */}
            <div className="flex justify-between items-center">
                <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Notes & Info
                </h2>
                <button
                    onClick={() => setAddingCard(true)}
                    className="text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors shadow-sm flex items-center gap-1"
                >
                    <Plus size={11} />
                    New Card
                </button>
            </div>

            {/* Add Card Form */}
            {addingCard && (
                <div className={`
                    rounded-xl border p-4 card-enter
                    ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-200'}
                `}>
                    <input
                        type="text"
                        value={newCardTitle}
                        onChange={(e) => setNewCardTitle(e.target.value)}
                        placeholder="Card title, e.g. Background, Abilities, Relationships..."
                        className={`
                            w-full px-3 py-2 rounded-lg text-xs outline-none
                            ${isDarkMode
                                ? 'bg-slate-700/50 border border-slate-600 text-slate-100 focus:border-indigo-500 placeholder-slate-500'
                                : 'bg-slate-50 border border-slate-200 text-slate-800 focus:border-indigo-500 placeholder-slate-400'
                            }
                        `}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleAddCard(); }
                            if (e.key === 'Escape') { setNewCardTitle(''); setAddingCard(false); }
                        }}
                    />
                    <div className="flex gap-2 mt-2.5">
                        <button onClick={handleAddCard} disabled={!newCardTitle.trim()} className="text-[10px] px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-40">
                            Create Card
                        </button>
                        <button onClick={() => { setNewCardTitle(''); setAddingCard(false); }} className={`text-[10px] px-3 py-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Loading Skeletons */}
            {loading && !notes.length && (
                <div className="columns-1 xs:columns-2 md:columns-3 xl:columns-4 gap-4 pb-20 space-y-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className={`
                            break-inside-avoid mb-4 rounded-xl border overflow-hidden p-3 space-y-3
                            ${isDarkMode ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-200'}
                        `}>
                            {/* Header */}
                            <div className="flex justify-between items-center mb-3 border-b pb-2 border-slate-100/10">
                                <Skeleton className="h-4 w-1/3 rounded" isDarkMode={isDarkMode} />
                                <div className="flex gap-2">
                                    <Skeleton className="h-4 w-4 rounded-full" isDarkMode={isDarkMode} />
                                    <Skeleton className="h-4 w-4 rounded-full" isDarkMode={isDarkMode} />
                                </div>
                            </div>
                            {/* Items */}
                            <div className="space-y-2">
                                <Skeleton className="h-3 w-full rounded" isDarkMode={isDarkMode} />
                                <Skeleton className="h-3 w-3/4 rounded" isDarkMode={isDarkMode} />
                                <Skeleton className="h-3 w-5/6 rounded" isDarkMode={isDarkMode} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Error */}
            {error && !loading && (
                <div className={`px-4 py-3 rounded-xl text-xs ${isDarkMode ? 'bg-rose-900/20 border border-rose-800/30 text-rose-400' : 'bg-rose-50 border border-rose-200 text-rose-500'}`}>
                    Failed to load notes: {error}
                </div>
            )}

            {/* Masonry Grid */}
            {!loading && !error && (
                <div className="masonry-grid">
                    {notes.map((card) => (
                        <NoteCard
                            key={card.id}
                            card={card}
                            onUpdate={updateNote}
                            onDelete={handleDeleteRequest}
                            onAddItem={addItem}
                            onMoveItem={moveItem}
                            isDarkMode={isDarkMode}
                            draggingId={draggingId}
                            onDragStart={setDraggingId}
                            onDragEnd={() => setDraggingId(null)}
                            onPreviewImage={setPreviewImage}
                        />
                    ))}

                </div>
            )}

            {/* Empty */}
            {!loading && !error && notes.length === 0 && !addingCard && (
                <div className={`
                    flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed
                    ${isDarkMode ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400'}
                `}>
                    <p className="text-xs mb-3">No notes for this character yet</p>
                    <button onClick={() => setAddingCard(true)} className="text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                        + Create First Card
                    </button>
                </div>
            )}

            {/* Image Preview Lightbox */}
            <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />

            {/* Edit Character Modal */}
            <EditCharacterModal
                isOpen={!!editingCharacter}
                onClose={() => setEditingCharacter(null)}
                onSubmit={handleEditSubmit}
                onUploadImage={uploadImage}
                character={editingCharacter}
                isDarkMode={isDarkMode}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={confirmModal.confirmText}
                confirmColor={confirmModal.confirmColor}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

export default CharacterDetailContent;
